import { enhancedApi as generatedBillingApi } from '@store/generated/billing';
import { enhancedApi as claimsApi } from '@store/generated/claims';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Extract filename from Content-Disposition header.
 * Handles both `filename="..."` and `filename=...` formats.
 */
function parseContentDispositionFilename(header: string | null): string | null {
  if (!header) return null;
  const match = header.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
  return match ? match[1].replace(/['"]/g, '').trim() : null;
}

/**
 * Shared responseHandler for X12 file download endpoints.
 * Returns blob + parsed filename from Content-Disposition header.
 */
async function x12DownloadResponseHandler(
  response: Response,
  fallbackFilename: string,
): Promise<{ blob: Blob; filename: string }> {
  if (!response.ok) {
    const error = (await response.json()) as { message?: string };
    throw new Error(error?.message || 'Failed to download file');
  }
  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition');
  const filename = parseContentDispositionFilename(disposition) || fallbackFilename;
  return { blob, filename };
}

/**
 * Feature-specific tag types for cache management
 */
const BILLING_TAG_TYPES = [
  'Claim',
  'ClaimsList',
  'BillingClaimsList',
  'ClaimQueueCounts',
  'BillingEncounter',
  'BillingEncountersList',
  'ClaimPdf',
  'FeeSchedule',
  // Cross-feature: invalidate encounter cache when claims change
  'Encounter',
] as const;

/**
 * Claims API - extends the generated claims OpenAPI client
 * This ensures we're using generated types and endpoints
 * while maintaining feature-specific customization
 */
const extendedClaimsApi = claimsApi
  .enhanceEndpoints({
    addTagTypes: BILLING_TAG_TYPES,
    endpoints: {
      // Enhance generated endpoints with cache tags
      getV2ProviderClaims: {
        providesTags: result =>
          result?.claims
            ? [
                ...result.claims.map(claim => ({
                  type: 'Claim' as const,
                  id: claim.id ?? 'unknown',
                })),
                { type: 'ClaimsList' as const, id: 'LIST' },
              ]
            : [{ type: 'ClaimsList' as const, id: 'LIST' }],
      },

      postV2ProviderClaims: {
        invalidatesTags: [
          { type: 'ClaimsList', id: 'LIST' },
          { type: 'BillingClaimsList', id: 'LIST' },
          { type: 'ClaimQueueCounts', id: 'LIST' },
          { type: 'BillingEncountersList', id: 'LIST' },
          { type: 'Encounter' },
        ],
      },

      getV2ProviderClaimsByClaimId: {
        providesTags: (_result, _error, arg) => [{ type: 'Claim', id: arg.claimId }],
      },

      putV2ProviderClaimsByClaimId: {
        invalidatesTags: (_result, _error, arg) => [
          { type: 'Claim', id: arg.claimId },
          { type: 'ClaimsList', id: 'LIST' },
          { type: 'BillingClaimsList', id: 'LIST' },
          { type: 'ClaimQueueCounts', id: 'LIST' },
        ],
      },

      deleteV2ProviderClaimsByClaimId: {
        invalidatesTags: (_result, _error, arg) => [
          { type: 'Claim', id: arg.claimId },
          { type: 'ClaimsList', id: 'LIST' },
          { type: 'BillingClaimsList', id: 'LIST' },
          { type: 'ClaimQueueCounts', id: 'LIST' },
        ],
      },

      getV2ProviderClaimsByClaimIdPdf: {
        providesTags: (_result, _error, arg) => [{ type: 'ClaimPdf', id: arg.claimId }],
      },

      postV2ProviderClaimsByClaimIdScrub: {
        // Don't invalidate Claim tag - scrub doesn't modify the claim on server
        // This prevents unnecessary refetch that triggers autosave
        invalidatesTags: () => [],
        // Update the claim in cache with scrub results
        async onQueryStarted(arg, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            // Update the specific claim query cache with the scrub results
            dispatch(
              billingApi.util.updateQueryData('getV2ProviderClaimsByClaimId', 
                { claimId: arg.claimId }, 
                () => {
                  // The scrub response is the updated claim
                  return data;
                }
              )
            );
          } catch {
            // Error handled by mutation hook
          }
        },
      },

      postV2ProviderClaimsByClaimIdRebuild: {
        // Invalidate claim and list since rebuild regenerates the claim
        invalidatesTags: (result, error, arg) =>
          result ? [
            { type: 'Claim', id: arg.claimId },
            { type: 'ClaimsList', id: 'LIST' },
            { type: 'BillingClaimsList', id: 'LIST' },
            { type: 'ClaimQueueCounts', id: 'LIST' },
          ] : [],
      },

      postV2ProviderClaimsByClaimIdResubmit: {
        // Invalidate original claim (has_children changes) + all lists + queue counts
        invalidatesTags: (_result, _error, arg) => [
          { type: 'Claim', id: arg.claimId },
          { type: 'ClaimsList', id: 'LIST' },
          { type: 'BillingClaimsList', id: 'LIST' },
          { type: 'ClaimQueueCounts', id: 'LIST' },
        ],
      },

      postV2ProviderClaimsByClaimIdSubmit: {
        // Invalidate claim and list since submit changes claim status
        invalidatesTags: (result, error, arg) =>
          result ? [
            { type: 'Claim', id: arg.claimId },
            { type: 'ClaimsList', id: 'LIST' },
            { type: 'BillingClaimsList', id: 'LIST' },
            { type: 'ClaimQueueCounts', id: 'LIST' },
          ] : [],
        // Update the claim in cache with submit results
        async onQueryStarted(arg, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            // Update the specific claim query cache with the submit results
            if (data?.claim) {
              dispatch(
                billingApi.util.updateQueryData('getV2ProviderClaimsByClaimId', 
                  { claimId: arg.claimId }, 
                  () => {
                    // The submit response contains the updated claim
                    return data.claim;
                  }
                )
              );
            }
          } catch {
            // Error handled by mutation hook
          }
        },
      },
    },
  });

/**
 * Fee Schedule API - extends the generated billing OpenAPI client
 * Handles baseline CPT rates for the organization
 */
const extendedBillingApi = generatedBillingApi
  .enhanceEndpoints({
    addTagTypes: ['FeeSchedule', 'BillingEncounter', 'BillingEncountersList', 'BillingClaimsList', 'ClaimQueueCounts', 'Claim', 'RemittancesList', 'RemittanceQueueCounts', 'BillingInvoices', 'PatientBalance', 'StatementsQueue', 'StatementsHistory'],
    endpoints: {
      // Org-scoped claims list (new enriched endpoint)
      getV2ProviderBillingClaims: {
        providesTags: result =>
          result?.claims
            ? [
                ...result.claims.map(claim => ({
                  type: 'Claim' as const,
                  id: claim.claim_id,
                })),
                { type: 'BillingClaimsList' as const, id: 'LIST' },
              ]
            : [{ type: 'BillingClaimsList' as const, id: 'LIST' }],
      },

      // Queue badge counts
      getV2ProviderBillingClaimsQueueCounts: {
        providesTags: [{ type: 'ClaimQueueCounts' as const, id: 'LIST' }],
      },

      // Org-scoped claim detail
      getV2ProviderBillingClaimsByClaimId: {
        providesTags: (_result, _error, arg) => [{ type: 'Claim' as const, id: arg.claimId }],
      },

      // Encounters list
      getV2ProviderBillingEncounters: {
        providesTags: result =>
          result?.encounters
            ? [
                ...result.encounters.map(({ encounter_id }) => ({
                  type: 'BillingEncounter' as const,
                  id: encounter_id,
                })),
                { type: 'BillingEncountersList' as const, id: 'LIST' },
              ]
            : [{ type: 'BillingEncountersList' as const, id: 'LIST' }],
      },

      // Single encounter detail
      getV2ProviderBillingEncountersByEncounterId: {
        providesTags: (_result, _error, arg) => [
          { type: 'BillingEncounter' as const, id: arg.encounterId },
        ],
      },

      // GET baseline rates
      getV2ProviderBillingFeeSchedulesBaseline: {
        providesTags: [{ type: 'FeeSchedule' as const, id: 'LIST' }],
      },

      // PUT baseline rates (bulk upsert)
      putV2ProviderBillingFeeSchedulesBaseline: {
        invalidatesTags: [{ type: 'FeeSchedule' as const, id: 'LIST' }],
      },

      // DELETE single baseline rate by code ID
      deleteV2ProviderBillingFeeSchedulesBaselineByCodeId: {
        invalidatesTags: [{ type: 'FeeSchedule' as const, id: 'LIST' }],
      },

      // Import fee schedule from extraction result
      postV2ProviderBillingFeeSchedulesFromExtraction: {
        invalidatesTags: [{ type: 'FeeSchedule' as const, id: 'LIST' }],
      },

      // Remittances list
      getV2ProviderBillingRemittances: {
        providesTags: result =>
          result?.remittances
            ? [
                ...result.remittances.map(r => ({
                  type: 'RemittancesList' as const,
                  id: r.remittance_id,
                })),
                { type: 'RemittancesList' as const, id: 'LIST' },
              ]
            : [{ type: 'RemittancesList' as const, id: 'LIST' }],
      },

      // Remittance queue badge counts
      getV2ProviderBillingRemittancesQueueCounts: {
        providesTags: [{ type: 'RemittanceQueueCounts' as const, id: 'LIST' }],
      },

      // Patient balance
      getV2ProviderBillingPatientsByPatientIdBalance: {
        providesTags: (_result, _error, { patientId }) => [
          { type: 'PatientBalance' as const, id: patientId },
        ],
      },

      // Record manual payment → refresh balance + encounter billing
      postV2ProviderBillingPatientsByPatientIdPayments: {
        invalidatesTags: (_result, _error, { patientId }) => [
          { type: 'PatientBalance' as const, id: patientId },
          { type: 'BillingEncounter' as const },
          { type: 'BillingEncountersList' as const, id: 'LIST' },
        ],
      },

      // Capture self-pay charges → refresh balance + encounter billing + encounters list
      postV2ProviderBillingCharges: {
        invalidatesTags: [
          { type: 'PatientBalance' as const },
          { type: 'BillingEncounter' as const },
          { type: 'BillingEncountersList' as const, id: 'LIST' },
        ],
      },

      // Statements queue
      getV2ProviderBillingStatementsQueue: {
        providesTags: [{ type: 'StatementsQueue' as const, id: 'LIST' }],
      },

      // Statements history
      getV2ProviderBillingStatementsHistory: {
        providesTags: [{ type: 'StatementsHistory' as const, id: 'LIST' }],
      },

      // Org-wide invoice list (local DB)
      getV2ProviderBillingInvoices: {
        providesTags: result =>
          result?.invoices
            ? [
                ...result.invoices.map(invoice => ({
                  type: 'BillingInvoices' as const,
                  id: invoice.id,
                })),
                { type: 'BillingInvoices' as const, id: 'LIST' },
              ]
            : [{ type: 'BillingInvoices' as const, id: 'LIST' }],
      },
    },
  })
  .injectEndpoints({
    endpoints: build => ({
      // Single ERA (835) download
      downloadRemittanceEra: build.query<
        { blob: Blob; filename: string },
        { remittanceId: string }
      >({
        query: ({ remittanceId }) => ({
          url: `/v2/provider/billing/remittances/${remittanceId}/download`,
          responseHandler: (response: Response) =>
            x12DownloadResponseHandler(response, `ERA_${remittanceId}.835`),
        }),
        keepUnusedDataFor: 0,
      }),

      // Bulk ERA (835) download — returns ZIP
      downloadRemittanceErasBulk: build.mutation<
        { blob: Blob; filename: string },
        { remittanceIds: string[] }
      >({
        query: ({ remittanceIds }) => ({
          url: '/v2/provider/billing/remittances/download',
          method: 'POST',
          body: { remittance_ids: remittanceIds },
          responseHandler: (response: Response) => {
            const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
            return x12DownloadResponseHandler(response, `ERA_${today}.zip`);
          },
        }),
      }),
    }),
  });

/**
 * Manually injected endpoints not yet in the OpenAPI spec
 */
const injectedClaimsApi = extendedClaimsApi.injectEndpoints({
  endpoints: build => ({
    getStediPdf: build.query<
      import('@store/generated/claims').ClaimPdfGenerateResponse,
      { claimId: string; background?: boolean }
    >({
      query: ({ claimId, background = true }) => ({
        url: `/v2/provider/claims/${claimId}/stedi-pdf`,
        params: { background },
      }),
      providesTags: (_result, _error, arg) => [{ type: 'ClaimPdf' as const, id: `stedi-${arg.claimId}` }],
    }),
    batchSubmitClaims: build.mutation<
      { submitted: Array<{ claim_id: string }>; failed: Array<{ claim_id: string; error: string }> },
      { batchSubmitClaimsRequest: { claim_ids: string[] } }
    >({
      query: ({ batchSubmitClaimsRequest }) => ({
        url: '/v2/provider/claims/batch-submit',
        method: 'POST',
        body: batchSubmitClaimsRequest,
      }),
      invalidatesTags: [
        { type: 'ClaimsList' as const, id: 'LIST' },
        { type: 'BillingClaimsList' as const, id: 'LIST' },
        { type: 'ClaimQueueCounts' as const, id: 'LIST' },
      ],
    }),

    // Single claim X12 (837) download
    downloadClaimX12: build.query<
      { blob: Blob; filename: string },
      { claimId: string }
    >({
      query: ({ claimId }) => ({
        url: `/v2/provider/claims/${claimId}/download`,
        responseHandler: (response: Response) =>
          x12DownloadResponseHandler(response, `Claim_${claimId}.837`),
      }),
      keepUnusedDataFor: 0,
    }),

    // Bulk claim X12 (837) download — returns ZIP
    downloadClaimsX12Bulk: build.mutation<
      { blob: Blob; filename: string },
      { claimIds: string[] }
    >({
      query: ({ claimIds }) => ({
        url: '/v2/provider/claims/download',
        method: 'POST',
        body: { claim_ids: claimIds },
        responseHandler: (response: Response) => {
          const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
          return x12DownloadResponseHandler(response, `Claims_${today}.zip`);
        },
      }),
    }),
  }),
});

// Export hooks with user-friendly names
export const {
  // Claims queries and mutations
  useGetV2ProviderClaimsQuery: useGetClaimsQuery,
  useLazyGetV2ProviderClaimsQuery: useLazyGetClaimsQuery,
  usePostV2ProviderClaimsMutation: useCreateClaimMutation,
  useGetV2ProviderClaimsByClaimIdQuery: useGetClaimByIdQuery,
  useLazyGetV2ProviderClaimsByClaimIdQuery: useLazyGetClaimByIdQuery,
  usePutV2ProviderClaimsByClaimIdMutation: useUpdateClaimMutation,
  useDeleteV2ProviderClaimsByClaimIdMutation: useDeleteClaimMutation,

  // PDF generation
  useGetV2ProviderClaimsByClaimIdPdfQuery: useGetClaimPdfQuery,
  useLazyGetV2ProviderClaimsByClaimIdPdfQuery: useLazyGetClaimPdfQuery,
  useLazyGetStediPdfQuery,

  // Claim scrubbing
  usePostV2ProviderClaimsByClaimIdScrubMutation: useClaimScrubMutation,

  // Claim rebuild
  usePostV2ProviderClaimsByClaimIdRebuildMutation: useClaimRebuildMutation,

  // Claim submission
  usePostV2ProviderClaimsByClaimIdSubmitMutation: useClaimSubmitMutation,

  // Claim resubmit (correction/void)
  usePostV2ProviderClaimsByClaimIdResubmitMutation: useResubmitClaimMutation,

  // Batch submission
  useBatchSubmitClaimsMutation,

  // Claim X12 (837) downloads
  useLazyDownloadClaimX12Query,
  useDownloadClaimsX12BulkMutation,
} = injectedClaimsApi;

// Export encounter, fee schedule, and new billing claims hooks with user-friendly names
export const {
  // Org-scoped billing claims (new enriched endpoints)
  useGetV2ProviderBillingClaimsQuery: useGetBillingClaimsListQuery,
  useGetV2ProviderBillingClaimsQueueCountsQuery: useGetClaimQueueCountsQuery,
  useGetV2ProviderBillingClaimsByClaimIdQuery: useGetBillingClaimDetailQuery,

  // Billing encounters list
  useGetV2ProviderBillingEncountersQuery: useGetBillingEncountersQuery,
  useLazyGetV2ProviderBillingEncountersQuery: useLazyGetBillingEncountersQuery,

  // Single encounter detail
  useGetV2ProviderBillingEncountersByEncounterIdQuery: useGetEncounterDetailQuery,

  // Encounter readiness (lightweight ready-to-bill signals)
  useGetV2ProviderBillingEncountersByEncounterIdReadinessQuery: useGetEncounterReadinessQuery,

  // Fee schedule queries and mutations
  useGetV2ProviderBillingFeeSchedulesBaselineQuery: useGetBaselineFeeScheduleQuery,
  usePutV2ProviderBillingFeeSchedulesBaselineMutation: useUpdateBaselineFeeScheduleMutation,
  useDeleteV2ProviderBillingFeeSchedulesBaselineByCodeIdMutation: useDeleteBaselineFeeScheduleMutation,

  // Remittances
  useGetV2ProviderBillingRemittancesQuery: useGetProviderRemittancesQuery,
  useGetV2ProviderBillingRemittancesByRemittanceIdQuery: useGetProviderRemittanceDetailQuery,
  useGetV2ProviderBillingRemittancesClaimsByClaimIdSummaryQuery: useGetProviderClaimRemittanceSummaryQuery,
  useGetV2ProviderBillingRemittancesQueueCountsQuery: useGetRemittanceQueueCountsQuery,

  // Fee schedule import from extraction
  usePostV2ProviderBillingFeeSchedulesFromExtractionMutation: useImportFeeScheduleFromExtractionMutation,

  // Org-wide invoice list (local DB)
  useGetV2ProviderBillingInvoicesQuery: useGetBillingInvoicesQuery,

  // Patient balance
  useGetV2ProviderBillingPatientsByPatientIdBalanceQuery: useGetPatientBalanceQuery,

  // Patient payment mutations
  usePostV2ProviderBillingPatientsByPatientIdPaymentsMutation: useRecordPaymentMutation,
  usePostV2ProviderBillingChargesMutation: useCaptureChargesMutation,

  // CPT charge lookup
  usePostV2ProviderBillingCptChargesLookupMutation: useCptChargesLookupMutation,

  // Statements
  useGetV2ProviderBillingStatementsQueueQuery: useGetStatementsQueueQuery,
  useGetV2ProviderBillingStatementsHistoryQuery: useGetStatementsHistoryQuery,

  // ERA (835) downloads
  useLazyDownloadRemittanceEraQuery,
  useDownloadRemittanceErasBulkMutation,
} = extendedBillingApi;

// Export claims API as billingApi for backward compatibility
export const billingApi = extendedClaimsApi;

// Export billing encounters API for selectors
export const billingEncountersApi = extendedBillingApi;
